import { type Condition, ResourceBlockerType } from '@repo/api-gw-sdk';

import { ACTION_ITEMS_PROPERTY } from '@/components/queryBuilder/propertyNames';
import {
  type ActionRequiredBlockerType,
  isActionItemBlockerType,
} from '@/data/inventory/resourceActionStatus';
import {
  createValueCondition,
  findValueCondition,
} from '@/types/advanceFilter';

import {
  actionItemLevelOf,
  type AlertLevel,
  LAST_SNAPSHOT_PARTIAL_TITLE,
} from './alertsModel';

// The value the inventory's action items filter takes for a resource whose last snapshot was partial; every other
// action item is named by its blocker type.
const LAST_SNAPSHOT_PARTIAL_ACTION_ITEM = 'LAST_SNAPSHOT_PARTIAL';

// Keyed by every ACTION_REQUIRED_* blocker type, so a type added to the API fails the type check until it is named.
const ACTION_ITEM_LABELS: Record<ActionRequiredBlockerType, string> = {
  [ResourceBlockerType.ACTION_REQUIRED_AZURE_SQL_VM_INVALID_CONFIGURATION]:
    'Azure SQL VM needs PiTR configuration',
  [ResourceBlockerType.ACTION_REQUIRED_SAP_HANA_BACKUP_NOT_CONFIGURED]:
    'SAP HANA agent not configured',
  [ResourceBlockerType.ACTION_REQUIRED_GCP_SQL_VM_BACKUP_NOT_CONFIGURED]:
    'SQL Server backups not reaching Eon',
  [ResourceBlockerType.ACTION_REQUIRED_GCP_SQL_VM_BACKUP_APPROACHING_CAPACITY]:
    'SQL Server backup approaching capacity',
  [ResourceBlockerType.ACTION_REQUIRED_GCP_SQL_VM_INVALID_CONFIGURATION]:
    'GCP SQL VM needs PiTR configuration',
  [ResourceBlockerType.ACTION_REQUIRED_DYNAMO_DB_INVALID_STREAM_VIEW_TYPE]:
    'Invalid DynamoDB stream view type',
  [ResourceBlockerType.ACTION_REQUIRED_DYNAMO_DB_PITR_NOT_ENABLED]:
    'DynamoDB PiTR not enabled',
  [ResourceBlockerType.ACTION_REQUIRED_DYNAMO_DB_INSUFFICIENT_RCUS]:
    'Insufficient DynamoDB read capacity',
  [ResourceBlockerType.ACTION_REQUIRED_RDS_DATABASE_STOPPED]:
    'RDS database stopped',
  [ResourceBlockerType.ACTION_REQUIRED_OBJECT_STORE_INVENTORY_REQUIRED]:
    'S3 inventory required',
  [ResourceBlockerType.ACTION_REQUIRED_OBJECT_STORE_INVENTORY_SCP_DENIED_PUBLIC_ACCESS_BLOCK]:
    'S3 inventory blocked by an SCP',
  [ResourceBlockerType.ACTION_REQUIRED_CLOUD_SQL_DATABASE_STOPPED]:
    'Cloud SQL instance stopped',
  [ResourceBlockerType.ACTION_REQUIRED_BIGQUERY_CHANGE_HISTORY_DISABLED]:
    'BigQuery change history disabled',
  [ResourceBlockerType.ACTION_REQUIRED_RDS_GLOBAL_DB_SECONDARY_CLUSTER]:
    'Aurora global database secondary cluster',
  [ResourceBlockerType.ACTION_REQUIRED_KEYSPACES_CDC_NOT_ENABLED]:
    'Keyspaces CDC not enabled',
  [ResourceBlockerType.ACTION_REQUIRED_AWS_NATIVE_PITR_PERMISSIONS_MISSING]:
    'AWS native PiTR permissions missing',
  [ResourceBlockerType.ACTION_REQUIRED_RDS_MANAGED_BY_EXTERNAL_BACKUP_PLAN]:
    'Managed by an external AWS Backup plan',
  [ResourceBlockerType.ACTION_REQUIRED_SAP_HANA_AGENT_APPLICATION_NOT_ONBOARDED]:
    'SAP HANA agent application not onboarded',
  [ResourceBlockerType.ACTION_REQUIRED_AZURE_FILES_STORAGE_ACCOUNT_IN_EXTERNAL_VAULT]:
    'Storage account registered with another vault',
  [ResourceBlockerType.ACTION_REQUIRED_AZURE_FILES_BACKUP_PERMISSION_MISSING]:
    'Azure Files backup permissions missing',
  [ResourceBlockerType.ACTION_REQUIRED_AZURE_FILES_BACKUP_NETWORK_ACCESS_BLOCKED]:
    'Azure Files backup network access blocked',
  [ResourceBlockerType.ACTION_REQUIRED_AZURE_VM_NATIVE_BACKUP_PERMISSION_MISSING]:
    'Azure VM backup permissions missing',
  [ResourceBlockerType.ACTION_REQUIRED_DYNAMO_DB_STREAMS_PERMISSIONS_MISSING]:
    'DynamoDB Streams permissions missing',
  [ResourceBlockerType.ACTION_REQUIRED_DYNAMO_DB_EXPORT_PERMISSIONS_MISSING]:
    'DynamoDB export permissions missing',
  [ResourceBlockerType.ACTION_REQUIRED_DYNAMO_DB_INCREMENTAL_EXPORT_REQUIRES_EXPORT_BASE]:
    'DynamoDB incremental export needs an export base',
  [ResourceBlockerType.ACTION_REQUIRED_DYNAMO_DB_KMS_KEY_INACCESSIBLE]:
    'DynamoDB KMS key inaccessible',
  [ResourceBlockerType.ACTION_REQUIRED_SHOVAL_AGENT_NOT_RUNNING]:
    'Backup sensor not running',
  [ResourceBlockerType.ACTION_REQUIRED_SHOVAL_AGENT_MISSING_PERMISSIONS]:
    'Backup sensor permissions missing',
  [ResourceBlockerType.ACTION_REQUIRED_SHOVAL_AGENT_REJECTED_ATTACH]:
    'Backup sensor rejected volume tracking',
  [ResourceBlockerType.ACTION_REQUIRED_SHOVAL_AGENT_NO_HF_POLICY]:
    'Backup sensor without a high-frequency policy',
  [ResourceBlockerType.ACTION_REQUIRED_AWS_NATIVE_BACKUP_PERMISSIONS_MISSING]:
    'AWS Backup permissions missing',
  [ResourceBlockerType.ACTION_REQUIRED_AWS_BACKUP_RESOURCE_TYPE_NOT_OPTED_IN]:
    'AWS Backup resource type not opted in',
  [ResourceBlockerType.ACTION_REQUIRED_EKS_CLUSTER_CONFIGMAP_ONLY_AUTH]:
    'EKS cluster uses ConfigMap-only authentication',
  [ResourceBlockerType.ACTION_REQUIRED_GCP_VM_IMAGE_SHARING_DOMAIN_RESTRICTED]:
    'GCP image sharing restricted by domain policy',
  [ResourceBlockerType.ACTION_REQUIRED_KEYSPACES_INSUFFICIENT_RCUS]:
    'Insufficient Keyspaces read capacity',
  [ResourceBlockerType.ACTION_REQUIRED_PERMISSION_GROUP_NOT_GRANTED]:
    'Resource type discovery not granted',
  [ResourceBlockerType.ACTION_REQUIRED_AZURE_STORAGE_SCAN_PERMISSION_MISSING]:
    'Azure storage scan permission missing',
  [ResourceBlockerType.ACTION_REQUIRED_PERMISSION_GROUP_BACKUP_NOT_GRANTED]:
    'Resource type backup not granted',
  [ResourceBlockerType.ACTION_REQUIRED_BACKUP_READ_ALLOWLIST_EXCLUDED]:
    'Not in the account read allowlist',
};

interface ActionItemOption {
  id: string;
  name: string;
  level: AlertLevel;
}

const lastSnapshotPartialOption: ActionItemOption = {
  id: LAST_SNAPSHOT_PARTIAL_ACTION_ITEM,
  name: LAST_SNAPSHOT_PARTIAL_TITLE,
  level: 'medium',
};

export const ACTION_ITEM_OPTIONS: ActionItemOption[] = [
  ...Object.values(ResourceBlockerType)
    .filter(isActionItemBlockerType)
    .map((type) => ({
      id: type,
      name: ACTION_ITEM_LABELS[type],
      level: actionItemLevelOf(type),
    })),
  lastSnapshotPartialOption,
].sort((a, b) => a.name.localeCompare(b.name));

export const actionItemLabelOf = (id: string) =>
  ACTION_ITEM_OPTIONS.find((option) => option.id === id)?.name ?? id;

// Ticking items only changes which action items the condition lists, so a "contains none of" or "contains all of"
// condition keeps its operator.
export const withActionItems = (
  conditions: Condition[],
  selected: string[]
) => {
  const existing = findValueCondition(conditions, ACTION_ITEMS_PROPERTY);
  const rest = conditions.filter((c) => c.property !== ACTION_ITEMS_PROPERTY);
  if (!selected.length) {
    return rest;
  }
  return [
    ...rest,
    existing
      ? { ...existing, value: selected }
      : createValueCondition(ACTION_ITEMS_PROPERTY, selected),
  ];
};
